<template>
  <div
    class="bg p-3 mb-auto h-full overflow-auto bg-gradient-to-b bg-gray-800"
    id="style-1"
  >
    <!-- Header -->
    <div class="header-bar flex items-center justify-between mb-4 px-1">
      <div class="flex items-center gap-2">
        <span class="header-icon">
          <i class="fas fa-robot"></i>
        </span>
        <h2 class="header-title">หุ่นยนต์สังหาร</h2>
      </div>
      <span class="robot-count-badge">
        <i class="fas fa-circle-dot mr-1 text-green-400 text-xs"></i>
        {{ activeRobots.length }} ใช้งาน
      </span>
    </div>

    <!-- Robot List -->
    <div class="overflow-y-auto max-h-96 flex flex-col gap-3">
      <div
        v-for="robot in activeRobots"
        :key="robot.id"
        class="robot-card"
      >
        <!-- Card Top: Avatar + Info -->
        <div class="flex items-start gap-3 mb-3">
          <div class="avatar-wrapper">
            <img
              :src="robot.img || 'default-robot.png'"
              alt="Robot"
              class="robot-avatar"
            />
            <span class="avatar-ring"></span>
          </div>

          <div class="flex-1 min-w-0">
            <h3 class="robot-name truncate">
              <i class="fas fa-robot mr-1 text-purple-400 text-xs"></i>
              {{ robot.name || 'หุ่นยนต์สังหาร' }}
            </h3>
            <p class="info-row">
              <i class="fas fa-user mr-1 text-indigo-400"></i>
              {{ userNames[robot.ownerUid] || robot.ownerUid }}
            </p>
            <p class="info-row">
              <i class="fas fa-map-location-dot mr-1 text-indigo-400"></i>
              {{ robot.map || 'ไม่ระบุ' }}
            </p>
          </div>

          <!-- Rarity Badge -->
          <span class="rarity-badge">
            <i class="fas fa-star text-yellow-400 text-xs"></i>
            {{ robot.rarity || 1 }}
          </span>
        </div>

        <!-- HP Bar -->
        <div class="hp-section mb-3">
          <div class="flex items-center justify-between mb-1">
            <span class="stat-label">
              <i class="fas fa-heart text-rose-400 mr-1"></i>HP
            </span>
            <span class="hp-numbers">
              {{ currentHp(robot) }}
              <span class="text-gray-500">/</span>
              {{ calculatedStats(robot).maxHp }}
            </span>
          </div>
          <div class="hp-bar-track">
            <div
              class="hp-bar-fill"
              :style="{ width: `${hpPercent(robot)}%` }"
              :class="{
                'hp-danger': hpPercent(robot) < 25,
                'hp-warn': hpPercent(robot) >= 25 && hpPercent(robot) < 60,
                'hp-good': hpPercent(robot) >= 60
              }"
            ></div>
          </div>
        </div>

        <!-- Stats Grid -->
        <div class="stats-grid mb-3">
          <div class="stat-box">
            <i class="fas fa-hand-fist stat-icon text-orange-400"></i>
            <div class="stat-content">
              <span class="stat-name">โจมตี</span>
              <span class="stat-value">{{ calculatedStats(robot).atk }}</span>
            </div>
          </div>

          <div class="stat-box">
            <i class="fas fa-bolt stat-icon text-yellow-400"></i>
            <div class="stat-content">
              <span class="stat-name">คริติคอล</span>
              <span class="stat-value">{{ calculatedStats(robot).critical }}%</span>
            </div>
          </div>

          <div class="stat-box">
            <i class="fas fa-wind stat-icon text-cyan-400"></i>
            <div class="stat-content">
              <span class="stat-name">หลบหลีก</span>
              <span class="stat-value">{{ calculatedStats(robot).evasion }}%</span>
            </div>
          </div>

          <div class="stat-box">
            <i class="fas fa-shield-halved stat-icon text-sky-400"></i>
            <div class="stat-content">
              <span class="stat-name">ป้องกัน</span>
              <span class="stat-value">{{ calculatedStats(robot).defense }}</span>
            </div>
          </div>
        </div>

        <!-- Divider -->
        <div class="divider"></div>

        <!-- Recall Section -->
        <div class="flex items-center justify-between mt-2">
          <span class="recall-hint">
            <i class="fas fa-circle-check text-green-400 mr-1"></i>
            พร้อมเรียกคืน
          </span>
          <button
            @click="recallRobot(robot.id)"
            class="recall-btn"
            :class="{ 'recall-btn--loading': isRecalling[robot.id] }"
            :disabled="isRecalling[robot.id]"
          >
            <span v-if="!isRecalling[robot.id]">
              <i class="fas fa-rotate-left mr-1"></i>เรียกคืน
            </span>
            <span v-else>
              <i class="fas fa-spinner fa-spin mr-1"></i>กำลังเรียกคืน...
            </span>
          </button>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="activeRobots.length === 0" class="empty-state">
        <i class="fas fa-robot text-4xl text-gray-600 mb-2"></i>
        <p class="text-gray-500 text-sm">ไม่มีหุ่นยนต์สังหารที่กำลังทำงาน</p>
      </div>
    </div>
  </div>
</template>

<script>
import { projectFirestore, timestamp } from '@/firebase/config';
import { onMounted, ref } from 'vue';

const num = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

const asArray = (v) => (Array.isArray(v) ? v : []);
const asObject = (v) =>
  v && typeof v === 'object' && !Array.isArray(v) ? v : {};

const cleanUndefined = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(cleanUndefined);
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (value === undefined) return acc;
    acc[key] =
      value && typeof value === 'object' ? cleanUndefined(value) : value;
    return acc;
  }, {});
};

const normalizeStats = (stats = {}) => {
  const safe = asObject(stats);
  return {
    stamina: num(safe.stamina, 0),
    str: num(safe.str, 0),
    int: num(safe.int, 0),
    agi: num(safe.agi, 0),
    defense: num(safe.defense, 0),
    maxHp: num(safe.maxHp, 0),
    atk: num(safe.atk, 0),
    critical: num(safe.critical, 0),
    evasion: num(safe.evasion, 0),
  };
};

function buildV2ItemFromRobot(robot) {
  const itemUpgradeStats = normalizeStats(robot.itemUpgradeStats || {});
  const silverUpgradeStats = normalizeStats(robot.silverUpgradeStats || {});

  return cleanUndefined({
    templateId: robot.templateId || robot.name || 'หุ่นยนต์สังหาร',
    states: {
      locked: false,
      protected: !!robot.protected,
      rarity: typeof robot.rarity === 'number' ? robot.rarity : 1,
      rarityColors: asArray(robot.rarityColors),
      transfer: !!robot.transfer,
      decomposable: robot.decomposable ?? true,
      damageTaken: num(robot.damageTaken, 0),
      itemUpgradeStats,
      silverUpgradeStats,
      silverUpgrades: asArray(robot.silverUpgrades),
      statPoints: num(robot.statPoints, 0),
      upgradeCount: num(robot.upgradeCount, 0),
      maxUpgradeCount:
        typeof robot.maxUpgradeCount === 'number' ? robot.maxUpgradeCount : 0,
      upgradeMaterials: asArray(robot.upgradeMaterials),
      cardSlots: asArray(robot.cardSlots),
      cardStats: asObject(robot.cardStats),
      cooldownUntil: robot.cooldownUntil || null,
      source: 'killerRobotRecallClient',
      recalledFromKillerRobotId: robot.id || null,
      recalledFromMap: robot.map || null,
    },
  });
}

export default {
  setup() {
    const activeRobots = ref([]);
    const userNames = ref({});
    const isRecalling = ref({});

    onMounted(() => {
      projectFirestore
        .collection('killerRobots')
        .onSnapshot(async (snap) => {
          const robots = snap.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          activeRobots.value = robots;

          const uids = [
            ...new Set(robots.map((r) => r.ownerUid).filter(Boolean)),
          ];

          await Promise.all(
            uids.map(async (uid) => {
              if (userNames.value[uid]) return;
              try {
                const charSnap = await projectFirestore
                  .collection('characters')
                  .doc(uid)
                  .get();
                userNames.value[uid] =
                  charSnap.data()?.name ||
                  charSnap.data()?.characterName ||
                  uid;
              } catch {
                userNames.value[uid] = uid;
              }
            })
          );
        });
    });

    const calculatedStats = (robot) => {
      const b = normalizeStats(robot.stats || {});
      const i = normalizeStats(robot.itemUpgradeStats || {});
      const s = normalizeStats(robot.silverUpgradeStats || {});

      const stamina = num(b.stamina) + num(i.stamina) + num(s.stamina);
      const str = num(b.str) + num(i.str) + num(s.str);
      const int_ = num(b.int) + num(i.int) + num(s.int);
      const agi = num(b.agi) + num(i.agi) + num(s.agi);
      const defense = num(b.defense) + num(i.defense) + num(s.defense);

      return {
        maxHp: stamina * 20 + num(b.maxHp),
        atk: str * 2 + num(b.atk),
        critical: Math.floor(int_ / 2) + num(b.critical),
        evasion: Math.floor(agi / 2) + num(b.evasion),
        defense,
      };
    };

    const currentHp = (robot) =>
      Math.max(0, calculatedStats(robot).maxHp - num(robot.damageTaken, 0));

    const hpPercent = (robot) => {
      const maxHp = calculatedStats(robot).maxHp;
      if (!maxHp || maxHp <= 0) return 0;
      return Math.max(0, Math.min(100, (currentHp(robot) / maxHp) * 100));
    };

    const recallRobot = async (killerRobotId) => {
      isRecalling.value[killerRobotId] = true;
      try {
        const killerRobotRef = projectFirestore
          .collection('killerRobots')
          .doc(killerRobotId);
        const killerSnap = await killerRobotRef.get();

        if (!killerSnap.exists) {
          console.error('ไม่พบหุ่นยนต์');
          return;
        }

        const robot = { id: killerSnap.id, ...killerSnap.data() };
        const ownerUid = robot.ownerUid;

        if (!ownerUid) {
          console.error('robot has no ownerUid');
          return;
        }

        const ownerBagRef = projectFirestore.collection('bags').doc(ownerUid);
        const bagSnap = await ownerBagRef.get();
        const bagData = bagSnap.exists ? bagSnap.data() || {} : {};

        if (Array.isArray(bagData.items)) {
          alert('กระเป๋ายังไม่ใช่ระบบ Item V2 กรุณา migrateItems ก่อน');
          return;
        }

        const currentWeight = num(bagData.currentWeight, 0);
        const maxLoad = num(bagData.maxLoad, 20);
        const instanceId = projectFirestore.collection('_ids').doc().id;
        const robotItem = buildV2ItemFromRobot(robot);
        const addedWeight = num(robot.weight, 0);
        const newCurrentWeight = currentWeight + addedWeight;

        if (newCurrentWeight > maxLoad) {
          alert('กระเป๋าเต็ม ไม่สามารถเรียกคืนได้');
          return;
        }

        const now = timestamp?.now ? timestamp.now() : new Date();

        if (bagSnap.exists) {
          await ownerBagRef.update({
            [`items.${instanceId}`]: robotItem,
            currentWeight: newCurrentWeight,
            updatedAt: now,
          });
        } else {
          await ownerBagRef.set({
            items: { [instanceId]: robotItem },
            currentWeight: addedWeight,
            maxLoad: 20,
            createdAt: now,
            updatedAt: now,
          });
        }

        await killerRobotRef.delete();
      } catch (err) {
        console.error('Error recalling robot v2:', err);
        alert('เรียกคืนไม่สำเร็จ');
      } finally {
        isRecalling.value[killerRobotId] = false;
      }
    };

    return {
      activeRobots,
      userNames,
      isRecalling,
      calculatedStats,
      currentHp,
      hpPercent,
      recallRobot,
    };
  },
};
</script>

<style scoped>
/* ─── Background ─────────────────────────────────────────── */
.bg {
  background:
    url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2Fmanagerroom.png?alt=media&token=6b9348b6-9364-4ce5-bf58-6e7b9c5b12f1')
    no-repeat center center fixed,
    linear-gradient(to bottom, rgba(31, 41, 55, 0.8), rgba(0, 0, 0, 0.9));
  background-size: cover;
}

/* ─── Header ─────────────────────────────────────────────── */
.header-bar {
  border-bottom: 1px solid rgba(99, 102, 241, 0.25);
  padding-bottom: 10px;
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: rgba(99, 102, 241, 0.2);
  color: #a5b4fc;
  font-size: 14px;
  border: 1px solid rgba(99, 102, 241, 0.35);
}

.header-title {
  font-size: 15px;
  font-weight: 700;
  color: #c7d2fe;
  letter-spacing: 0.02em;
}

.robot-count-badge {
  font-size: 11px;
  color: #9ca3af;
  background: rgba(17, 24, 39, 0.6);
  border: 1px solid rgba(75, 85, 99, 0.5);
  padding: 2px 8px;
  border-radius: 999px;
}

/* ─── Robot Card ─────────────────────────────────────────── */
.robot-card {
  background: rgba(17, 24, 39, 0.75);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
  padding: 14px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.35);
  transition: border-color 0.2s;
}

.robot-card:hover {
  border-color: rgba(139, 92, 246, 0.45);
}

/* ─── Avatar ─────────────────────────────────────────────── */
.avatar-wrapper {
  position: relative;
  flex-shrink: 0;
}

.robot-avatar {
  width: 56px;
  height: 56px;
  border-radius: 10px;
  object-fit: cover;
  display: block;
  position: relative;
  z-index: 1;
}

.avatar-ring {
  position: absolute;
  inset: -2px;
  border-radius: 12px;
  background: linear-gradient(135deg, #7c3aed, #4f46e5);
  z-index: 0;
}

.avatar-wrapper .robot-avatar {
  position: relative;
  z-index: 1;
}

/* ─── Robot Info ─────────────────────────────────────────── */
.robot-name {
  font-size: 13px;
  font-weight: 700;
  color: #c4b5fd;
  margin-bottom: 3px;
}

.info-row {
  font-size: 11px;
  color: #6b7280;
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  gap: 2px;
}

.rarity-badge {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  font-weight: 700;
  color: #fbbf24;
  background: rgba(251, 191, 36, 0.1);
  border: 1px solid rgba(251, 191, 36, 0.25);
  border-radius: 6px;
  padding: 2px 7px;
  flex-shrink: 0;
}

/* ─── HP Bar ─────────────────────────────────────────────── */
.stat-label {
  font-size: 11px;
  color: #9ca3af;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.hp-numbers {
  font-size: 11px;
  font-weight: 700;
  color: #e5e7eb;
  font-variant-numeric: tabular-nums;
}

.hp-bar-track {
  height: 6px;
  background: rgba(55, 65, 81, 0.8);
  border-radius: 999px;
  overflow: hidden;
}

.hp-bar-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.4s ease, background-color 0.3s;
}

.hp-good {
  background: linear-gradient(90deg, #6d28d9, #8b5cf6);
  box-shadow: 0 0 8px rgba(139, 92, 246, 0.5);
}

.hp-warn {
  background: linear-gradient(90deg, #d97706, #f59e0b);
  box-shadow: 0 0 8px rgba(245, 158, 11, 0.4);
}

.hp-danger {
  background: linear-gradient(90deg, #b91c1c, #ef4444);
  box-shadow: 0 0 8px rgba(239, 68, 68, 0.5);
  animation: pulse-danger 1.2s ease-in-out infinite;
}

@keyframes pulse-danger {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

/* ─── Stats Grid ─────────────────────────────────────────── */
.stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}

.stat-box {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(31, 41, 55, 0.7);
  border: 1px solid rgba(55, 65, 81, 0.7);
  border-radius: 8px;
  padding: 7px 10px;
  transition: background 0.2s;
}

.stat-box:hover {
  background: rgba(55, 65, 81, 0.7);
}

.stat-icon {
  font-size: 13px;
  width: 16px;
  text-align: center;
  flex-shrink: 0;
}

.stat-content {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.stat-name {
  font-size: 10px;
  color: #6b7280;
  line-height: 1;
  margin-bottom: 1px;
}

.stat-value {
  font-size: 13px;
  font-weight: 700;
  color: #e5e7eb;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

/* ─── Divider ────────────────────────────────────────────── */
.divider {
  height: 1px;
  background: rgba(55, 65, 81, 0.6);
  margin: 4px 0;
}

/* ─── Recall Button ──────────────────────────────────────── */
.recall-hint {
  font-size: 11px;
  color: #6b7280;
  display: flex;
  align-items: center;
}

.recall-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(135deg, #059669, #10b981);
  border: none;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.1s, box-shadow 0.2s;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

.recall-btn:hover:not(:disabled) {
  opacity: 0.9;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
}

.recall-btn:active:not(:disabled) {
  transform: translateY(0);
}

.recall-btn--loading,
.recall-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  background: #374151;
  box-shadow: none;
}

/* ─── Empty State ────────────────────────────────────────── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 6px;
  opacity: 0.6;
}
</style>